import { useEffect, useMemo, useState } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { standardRateNow } from '../lib/taxes.ts';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Repairs: the counter where customers leave things to be fixed. The board shows what is in the
 * shop by where it stands (to look at, waiting for the customer's answer, to repair, on the bench,
 * ready to collect); a repair opens with its quote, the customer's answer, the work done, the
 * invoice and its history.
 */

type State = 'received' | 'quoted' | 'approved' | 'repairing' | 'waiting' | 'ready' | 'declined' | 'delivered' | 'cancelled';
type LineKind = 'part' | 'labour' | 'fee';
type Via = 'in_shop' | 'phone' | 'message';
type Tab = 'board' | 'list' | 'report' | 'settings';

interface Row {
  id: string;
  number: number;
  name: string;
  customer: string;
  customer_name: string;
  phone: string | null;
  contact: string | null;
  device: string;
  model: string | null;
  serial: string | null;
  state: State;
  priority: 'normal' | 'urgent';
  warranty: boolean;
  technician: string | null;
  technician_name: string | null;
  promised: string | null;
  created_at: string;
  ready_at: string | null;
  delivered_at: string | null;
  invoice: string | null;
  approved_total: number;
  total: number;
}
interface Line {
  id: string;
  kind: LineKind;
  product: string | null;
  name: string;
  qty: number;
  unit_price: number;
  tax_rate: number;
  net: number;
  tax: number;
  total: number;
}
interface Repair extends Row {
  accessories: string | null;
  problem: string;
  diagnosis: string | null;
  report: string | null;
  warranty_note: string | null;
  location: string | null;
  quoted_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  approved_via: Via | null;
  started_at: string | null;
  delivered_to: string | null;
  parts_taken: boolean;
  cancel_reason: string | null;
  received_by_name: string | null;
  invoice_name: string | null;
  invoice_state: string | null;
  lines: Line[];
  totals: { net: number; tax: number; total: number; charge: number };
  events: { kind: string; text: string | null; at: string; by_name: string | null }[];
  today: string;
  can: { receive: boolean; work: boolean; invoice: boolean; cancel: boolean };
}
interface Overview {
  today: string;
  kpi: { in_shop: number; to_check: number; waiting_answer: number; waiting_parts: number; to_collect: number; late: number; mine: number; delivered_30: number; turnaround: number | null };
  can: { receive: boolean; work: boolean; invoice: boolean; configure: boolean; reports: boolean };
}
interface Settings {
  hourly_rate: number;
  labour_name: string;
  labour_tax_rate: number;
  inspection_fee: number;
  inspection_name: string;
  promise_days: number;
}
interface Setup {
  customers: { id: string; name: string; phone: string | null }[];
  products: { id: string; name: string; price: number; tax_rate: number; net: number }[];
  locations: { id: string; name: string }[];
  technicians: { id: string; name: string }[];
  devices: string[];
  settings: Settings;
  stock: boolean;
}

const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  received: { tone: 'info', icon: 'box' },
  quoted: { tone: 'warn', icon: 'phone' },
  approved: { tone: 'primary', icon: 'checkCircle' },
  repairing: { tone: 'primary', icon: 'hammer' },
  waiting: { tone: 'warn', icon: 'pause' },
  ready: { tone: 'ok', icon: 'bell' },
  declined: { tone: 'neutral', icon: 'x' },
  delivered: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
/** The board's columns and the states each one holds. */
const COLUMNS: { key: string; icon: IconName; states: State[] }[] = [
  { key: 'received', icon: 'box', states: ['received'] },
  { key: 'quoted', icon: 'phone', states: ['quoted'] },
  { key: 'to_repair', icon: 'clock', states: ['approved', 'waiting'] },
  { key: 'repairing', icon: 'hammer', states: ['repairing'] },
  { key: 'collect', icon: 'bell', states: ['ready', 'declined'] },
];
const OPEN: State[] = ['received', 'quoted', 'approved', 'repairing', 'waiting', 'ready', 'declined'];
const BENCH: State[] = ['received', 'quoted', 'approved', 'repairing', 'waiting'];
const VIAS: Via[] = ['in_shop', 'phone', 'message'];
const KINDS: LineKind[] = ['part', 'labour', 'fee'];
const KIND_ICON: Record<LineKind, IconName> = { part: 'box', labour: 'clock', fee: 'receipt' };
const num = (n: number) => new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 }).format(n);
const days = (n: number | null) => (n === null ? '—' : n < 1 ? tn('rp.n_hours', Math.max(1, Math.round(n * 24))) : tn('rp.n_days', Math.round(n * 10) / 10));
const initials = (n: string | null) =>
  n
    ? n
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('')
    : '—';
const deviceOf = (r: Pick<Row, 'device' | 'model'>) => [r.device, r.model].filter(Boolean).join(' · ');
const lateOf = (r: Pick<Row, 'state' | 'promised'>, today: string) => BENCH.includes(r.state) && !!r.promised && r.promised < today;
const amountText = (minor: number) => (minor ? (minor / 100).toFixed(2) : '');

function StateBadge({ r }: { r: Pick<Row, 'state'> }) {
  return <StatusBadge tone={STATE[r.state].tone} icon={STATE[r.state].icon} label={t(`rp.st_${r.state}` as MessageKey)} />;
}

export function Repairs() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const { data: o, reload } = useAction<Overview>('repair.overview');
  const { data: setup, reload: reloadSetup } = useAction<Setup>('repair.setup');
  const tabs: { key: Tab; icon: IconName; show: boolean }[] = [
    { key: 'board', icon: 'layers', show: true },
    { key: 'list', icon: 'list', show: true },
    { key: 'report', icon: 'chart', show: can('repair.reports') },
    { key: 'settings', icon: 'settings', show: can('repair.configure') },
  ];
  const visible = tabs.filter((x) => x.show);
  const tab = (visible.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'board') as Tab;
  const open = params.get('r');
  const [form, setForm] = useState<{ id: string | null } | null>(null);
  const [version, setVersion] = useState(0);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports rp-page">
        <div className="page-head">
          <div>
            <h1>{t('rp.title')}</h1>
            <p className="muted">{t('rp.subtitle')}</p>
          </div>
          {can('repair.receive') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setForm({ id: null })}>
                {t('rp.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('rp.at_a_glance')}>
            <Kpi label={t('rp.k_in_shop')} value={String(o.kpi.in_shop)} hint={o.can.work ? tn('rp.k_mine', o.kpi.mine) : tn('rp.k_to_check', o.kpi.to_check)} />
            <Kpi label={t('rp.k_answer')} value={String(o.kpi.waiting_answer)} hint={t('rp.k_answer_hint')} warn={o.kpi.waiting_answer > 0} />
            <Kpi label={t('rp.k_parts')} value={String(o.kpi.waiting_parts)} hint={t('rp.k_parts_hint')} />
            <Kpi label={t('rp.k_collect')} value={String(o.kpi.to_collect)} hint={t('rp.k_collect_hint')} />
            <Kpi label={t('rp.k_late')} value={String(o.kpi.late)} hint={t('rp.k_late_hint')} warn={o.kpi.late > 0} />
            <Kpi label={t('rp.k_turnaround')} value={days(o.kpi.turnaround)} hint={tn('rp.k_delivered', o.kpi.delivered_30)} />
          </section>
        )}
        <div className="segmented tabs" role="tablist" aria-label={t('rp.sections')}>
          {visible.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'board' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`rp.tab_${x.key}` as MessageKey)}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'board' ? (
          <BoardTab key={version} today={o.today} canWork={o.can.work} onOpen={(id) => set({ r: id })} onNew={o.can.receive ? () => setForm({ id: null }) : undefined} />
        ) : tab === 'list' ? (
          <ListTab key={version} today={o.today} onOpen={(id) => set({ r: id })} />
        ) : tab === 'report' ? (
          <ReportTab today={o.today} />
        ) : (
          <SettingsTab
            onChange={async () => {
              await reloadSetup();
            }}
          />
        )}
      </div>
      {open && setup ? <RepairDialog key={`${open}-${version}`} id={open} setup={setup} onClose={() => set({ r: null })} onChange={changed} onEdit={(id) => setForm({ id })} /> : null}
      {form && setup ? (
        <ReceiveForm
          id={form.id}
          setup={setup}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await changed();
            await reloadSetup();
            set({ r: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- board and list --------------------------------------------------------------------------------

function RepairCard({ r, today, onOpen }: { r: Row; today: string; onOpen: () => void }) {
  const late = lateOf(r, today);
  return (
    <article className={`tk-task rp-card${r.priority === 'urgent' && BENCH.includes(r.state) ? ' urgent' : ''}`} draggable={false}>
      <button className="tk-task-main" onClick={onOpen}>
        <span className="mt-card-top small">
          <span className="muted tk-task-no">{r.name}</span>
          {r.priority === 'urgent' && BENCH.includes(r.state) ? <span className="mt-chip urgent">{t('rp.urgent')}</span> : null}
          {r.warranty ? (
            <span className="mt-chip rp-chip-warranty">
              <Icon name="shieldCheck" size={12} /> {t('rp.warranty')}
            </span>
          ) : null}
          {r.state === 'waiting' ? <span className="mt-chip high">{t('rp.st_waiting')}</span> : null}
          {r.state === 'declined' ? <span className="mt-chip">{t('rp.st_declined')}</span> : null}
        </span>
        <strong>{deviceOf(r)}</strong>
        <span className="small muted mt-card-where">
          <Icon name="contact" size={12} /> {r.customer_name}
        </span>
        <span className="tk-task-meta small">
          {r.promised && BENCH.includes(r.state) ? (
            <span className={late ? 'tk-warn' : 'muted'}>
              <Icon name="calendar" size={12} /> {late ? t('rp.late_since', { day: dayText(r.promised) }) : dayText(r.promised)}
            </span>
          ) : null}
          {r.total ? <span className="tabular">{money(r.total)}</span> : null}
          {r.state === 'ready' && !r.invoice && r.total ? <span className="tk-warn">{t('rp.to_invoice')}</span> : null}
          <span className="tk-avatar" title={r.technician_name ?? t('rp.nobody')} aria-label={r.technician_name ?? t('rp.nobody')}>
            {initials(r.technician_name)}
          </span>
        </span>
      </button>
    </article>
  );
}

function BoardTab({ today, canWork, onOpen, onNew }: { today: string; canWork: boolean; onOpen: (id: string) => void; onNew?: () => void }) {
  const [who, setWho] = useState<'' | 'me'>('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data } = useAction<{ today: string; repairs: Row[] }>('repair.list', { view: 'board', technician: who || null, search: search || null });
  return (
    <>
      <div className="toolbar tk-board-tools mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('rp.search')} aria-label={t('rp.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        {canWork ? (
          <div className="segmented" role="radiogroup" aria-label={t('rp.technician')}>
            {(['', 'me'] as const).map((w) => (
              <button key={w} type="button" role="radio" aria-checked={who === w} className={who === w ? 'active' : ''} onClick={() => setWho(w)}>
                {t(w === '' ? 'rp.everyone' : 'rp.mine_filter')}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : !data.repairs.length && !search && !who ? (
        <EmptyState
          icon="hammer"
          title={t('rp.empty_title')}
          body={t('rp.empty_body')}
          action={
            onNew ? (
              <Button variant="primary" icon="plus" onClick={onNew}>
                {t('rp.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="tk-board mt-board rp-board" style={{ '--cols': COLUMNS.length } as React.CSSProperties}>
          {COLUMNS.map((c) => {
            const cards = data.repairs.filter((r) => c.states.includes(r.state));
            return (
              <section key={c.key} className={`tk-col rp-col-${c.key}`} aria-label={t(`rp.col_${c.key}` as MessageKey)}>
                <header className="tk-col-head">
                  <Icon name={c.icon} size={16} />
                  <strong>{t(`rp.col_${c.key}` as MessageKey)}</strong>
                  <span className="muted small">{cards.length}</span>
                </header>
                <div className="tk-col-body">
                  {cards.map((r) => (
                    <RepairCard key={r.id} r={r} today={today} onOpen={() => onOpen(r.id)} />
                  ))}
                  {!cards.length ? <p className="muted small tk-col-empty">{t(`rp.col_empty_${c.key}` as MessageKey)}</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

const VIEWS = ['open', 'done', 'all'] as const;

function ListTab({ today, onOpen }: { today: string; onOpen: (id: string) => void }) {
  const [view, setView] = useState<(typeof VIEWS)[number]>('open');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data } = useAction<{ today: string; repairs: Row[] }>('repair.list', { view, search: search || null });
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('rp.search')} aria-label={t('rp.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label={t('rp.show')}>
          {VIEWS.map((v) => (
            <button key={v} type="button" role="radio" aria-checked={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
              {t(`rp.view_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          icon="download"
          disabled={!data?.repairs.length}
          onClick={() =>
            data &&
            downloadCsv(
              'repairs.csv',
              [t('rp.c_number'), t('rp.c_received'), t('rp.c_customer'), t('rp.c_item'), t('rp.c_serial'), t('rp.c_state'), t('rp.c_technician'), t('rp.c_total')],
              data.repairs.map((r) => [
                r.name,
                r.created_at.slice(0, 10),
                r.customer_name,
                deviceOf(r),
                r.serial ?? '',
                t(`rp.st_${r.state}` as MessageKey),
                r.technician_name ?? '',
                (r.total / 100).toFixed(2),
              ]),
            )
          }
        >
          {t('rp.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : (
        <TableWrap>
          <Table
            view="repairs.list"
            head={[t('rp.c_number'), t('rp.c_received'), t('rp.c_customer'), t('rp.c_item'), t('rp.c_state'), t('rp.c_promised'), t('rp.c_technician'), t('rp.c_total')]}
            phoneHide={[1, 5, 6]}
            empty={search ? t('rp.no_match') : t('rp.none_here')}
            rows={data.repairs.map((r) => ({
              key: r.id,
              onClick: () => onOpen(r.id),
              cells: [
                <strong key="n">{r.name}</strong>,
                dayText(r.created_at.slice(0, 10)),
                r.customer_name,
                <span key="d" className="rp-list-item">
                  {deviceOf(r)}
                  {r.serial ? <span className="muted small"> · {r.serial}</span> : null}
                </span>,
                <StateBadge key="s" r={r} />,
                r.promised && BENCH.includes(r.state) ? <span className={lateOf(r, today) ? 'text-danger' : undefined}>{dayText(r.promised)}</span> : '—',
                r.technician_name ?? '—',
                <span key="t" className="tabular">
                  {r.warranty ? t('rp.warranty') : r.total ? money(r.total) : '—'}
                </span>,
              ],
            }))}
          />
        </TableWrap>
      )}
    </>
  );
}

// ---- one repair ------------------------------------------------------------------------------------

type Step = 'approve' | 'decline' | 'wait' | 'finish' | 'reopen' | 'deliver' | 'cancel' | 'quote' | 'invoice';

function RepairDialog({ id, setup, onClose, onChange, onEdit }: { id: string; setup: Setup; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void }) {
  const { data: fetched } = useAction<Repair>('repair.get', { id });
  const [r, setR] = useState<Repair | null>(null);
  useEffect(() => setR(fetched), [fetched]);
  const { busy, run } = useSave();
  const [step, setStep] = useState<Step | null>(null);
  const [note, setNote] = useState('');
  const after = async (next: Repair) => {
    setR(next);
    setStep(null);
    await onChange();
  };
  const go = (name: string, input: Record<string, unknown>, done: string) =>
    void run(async () => {
      await after(await action<Repair>(name, { id, ...input }));
    }, done);

  if (!r)
    return (
      <Dialog title={t('rp.repair')} onClose={onClose} size="lg">
        <Skeleton h={420} />
      </Dialog>
    );
  const late = lateOf(r, r.today);
  const canQuote = r.can.work && BENCH.includes(r.state);
  const toPay = r.totals.charge > 0;
  const moves: React.ReactNode[] = [];
  const add = (key: string, node: React.ReactNode) => moves.push(<span key={key}>{node}</span>);
  if (r.state === 'received') {
    if (r.warranty && r.can.work)
      add(
        'start',
        <Button variant="primary" icon="hammer" busy={busy} onClick={() => go('repair.progress', { to: 'repairing' }, t('rp.started'))}>
          {t('rp.start')}
        </Button>,
      );
    if (!r.warranty && r.can.work && r.lines.length && r.diagnosis)
      add(
        'send',
        <Button variant="primary" icon="send" busy={busy} onClick={() => go('repair.send_quote', {}, t('rp.quote_sent'))}>
          {t('rp.send_quote')}
        </Button>,
      );
  }
  if ((r.state === 'quoted' || (r.state === 'received' && !r.warranty && r.lines.length)) && r.can.receive) {
    add(
      'yes',
      <Button variant={r.state === 'quoted' ? 'primary' : 'secondary'} icon="check" onClick={() => setStep('approve')}>
        {t('rp.said_yes')}
      </Button>,
    );
    add(
      'no',
      <Button icon="x" onClick={() => setStep('decline')}>
        {t('rp.said_no')}
      </Button>,
    );
  }
  if (r.state === 'approved' && r.can.work)
    add(
      'start',
      <Button variant="primary" icon="hammer" busy={busy} onClick={() => go('repair.progress', { to: 'repairing' }, t('rp.started'))}>
        {t('rp.start')}
      </Button>,
    );
  if (r.state === 'repairing' && r.can.work)
    add(
      'finish',
      <Button variant="primary" icon="checkCircle" onClick={() => (setNote(r.report ?? ''), setStep('finish'))}>
        {t('rp.finish')}
      </Button>,
    );
  if ((r.state === 'approved' || r.state === 'repairing') && r.can.work)
    add(
      'wait',
      <Button icon="pause" onClick={() => (setNote(''), setStep('wait'))}>
        {t('rp.wait')}
      </Button>,
    );
  if (r.state === 'waiting' && r.can.work)
    add(
      'resume',
      <Button variant="primary" icon="hammer" busy={busy} onClick={() => go('repair.progress', { to: 'repairing' }, t('rp.resumed'))}>
        {t('rp.resume')}
      </Button>,
    );
  if ((r.state === 'ready' || r.state === 'declined') && toPay && !r.invoice && r.can.invoice)
    add(
      'invoice',
      <Button variant="primary" icon="invoice" onClick={() => setStep('invoice')}>
        {t('rp.invoice')}
      </Button>,
    );
  if ((r.state === 'ready' || r.state === 'declined') && r.can.receive)
    add(
      'deliver',
      <Button variant={toPay && !r.invoice ? 'secondary' : 'primary'} icon="check" disabled={toPay && !r.invoice} onClick={() => (setNote(r.contact ?? r.customer_name), setStep('deliver'))}>
        {t('rp.deliver')}
      </Button>,
    );
  if (r.state === 'ready' && !r.invoice && r.can.work)
    add(
      'reopen',
      <Button icon="undo" onClick={() => (setNote(''), setStep('reopen'))}>
        {t('rp.reopen')}
      </Button>,
    );

  return (
    <>
      <Dialog
        title={`${r.name} · ${deviceOf(r)}`}
        onClose={onClose}
        size="lg"
        footer={
          <span className="foot-actions rp-foot">
            {r.can.receive && OPEN.includes(r.state) ? (
              <Button variant="ghost" icon="edit" onClick={() => onEdit(r.id)}>
                {t('rp.edit')}
              </Button>
            ) : null}
            {r.can.cancel && BENCH.includes(r.state) ? (
              <Button variant="ghost" icon="x" onClick={() => (setNote(''), setStep('cancel'))}>
                {t('rp.cancel')}
              </Button>
            ) : null}
            <span className="mt-foot-moves">{moves}</span>
          </span>
        }
      >
        <div className="mt-detail">
          <div className="mt-badges">
            <StateBadge r={r} />
            {r.priority === 'urgent' && BENCH.includes(r.state) ? <StatusBadge tone="danger" icon="flame" label={t('rp.urgent')} /> : null}
            {r.warranty ? <StatusBadge tone="info" icon="shieldCheck" label={r.warranty_note ? `${t('rp.warranty')} · ${r.warranty_note}` : t('rp.warranty')} /> : null}
            {late ? <StatusBadge tone="danger" icon="alert" label={t('rp.late_since', { day: dayText(r.promised!) })} /> : null}
          </div>
          {r.state === 'received' && !r.warranty ? <p className="callout callout-info">{r.lines.length && r.diagnosis ? t('rp.hint_send') : t('rp.hint_quote')}</p> : null}
          {r.state === 'quoted' ? (
            <p className="callout callout-warn">
              <Icon name="phone" size={16} />
              <span>{t('rp.hint_quoted', { total: money(r.totals.charge), when: r.quoted_at ? dateTime(r.quoted_at) : '' })}</span>
            </p>
          ) : null}
          {(r.state === 'ready' || r.state === 'declined') && toPay && !r.invoice ? <p className="callout callout-warn">{t('rp.hint_invoice_first')}</p> : null}
          <dl className="mt-facts">
            <div>
              <dt>{t('rp.customer')}</dt>
              <dd>
                <strong>{r.customer_name}</strong>
                {r.contact ? <span className="muted"> · {t('rp.brought_by', { name: r.contact })}</span> : null}
                {r.phone ? (
                  <>
                    <br />
                    <a href={`tel:${r.phone}`}>{r.phone}</a>
                  </>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{t('rp.item')}</dt>
              <dd>
                {deviceOf(r)}
                {r.serial ? <span className="muted"> · {r.serial}</span> : null}
              </dd>
            </div>
            {r.accessories ? (
              <div>
                <dt>{t('rp.accessories')}</dt>
                <dd>{r.accessories}</dd>
              </div>
            ) : null}
            <div>
              <dt>{t('rp.technician')}</dt>
              <dd>{r.technician_name ?? t('rp.nobody')}</dd>
            </div>
            <div>
              <dt>{t('rp.received')}</dt>
              <dd>
                {dateTime(r.created_at)}
                {r.received_by_name ? <span className="muted"> · {r.received_by_name}</span> : null}
              </dd>
            </div>
            {r.promised && r.state !== 'delivered' && r.state !== 'cancelled' ? (
              <div>
                <dt>{t('rp.promised')}</dt>
                <dd className={late ? 'text-danger' : undefined}>{dayText(r.promised)}</dd>
              </div>
            ) : null}
            {r.approved_at ? (
              <div>
                <dt>{t('rp.approved')}</dt>
                <dd>
                  {t('rp.approved_by', { name: r.approved_by ?? '—', via: r.approved_via ? t(`rp.via_${r.approved_via}` as MessageKey) : '' })}
                  <br />
                  <span className="muted small">
                    {dateTime(r.approved_at)} · {money(r.approved_total)}
                  </span>
                </dd>
              </div>
            ) : null}
            {r.delivered_at ? (
              <div>
                <dt>{t('rp.handed_back')}</dt>
                <dd>
                  {dateTime(r.delivered_at)}
                  {r.delivered_to ? <span className="muted"> · {r.delivered_to}</span> : null}
                </dd>
              </div>
            ) : null}
            {r.invoice ? (
              <div>
                <dt>{t('rp.invoice_label')}</dt>
                <dd>{r.invoice_name ? t('rp.invoice_no', { no: r.invoice_name }) : t('rp.invoice_draft')}</dd>
              </div>
            ) : null}
          </dl>
          <section>
            <h3 className="mt-h3">{t('rp.problem')}</h3>
            <p className="mt-desc">{r.problem}</p>
          </section>
          <section>
            <div className="mt-section-head">
              <h3 className="mt-h3">{r.warranty ? t('rp.work_parts') : t('rp.quote')}</h3>
              {canQuote ? (
                <Button size="sm" icon="edit" onClick={() => setStep('quote')}>
                  {r.lines.length || r.diagnosis ? t('rp.edit_quote') : t('rp.write_quote')}
                </Button>
              ) : null}
            </div>
            {r.diagnosis ? <p className="mt-desc">{r.diagnosis}</p> : null}
            {r.lines.length ? (
              <QuoteLines lines={r.lines} totals={r.totals} warranty={r.warranty} />
            ) : (
              <p className="muted small">{r.state === 'declined' ? t('rp.nothing_charged') : canQuote ? t('rp.no_quote_yet') : t('rp.no_lines')}</p>
            )}
            {r.parts_taken ? (
              <p className="muted small">
                <Icon name="box" size={12} /> {t('rp.parts_taken')}
              </p>
            ) : null}
          </section>
          {r.report ? (
            <section>
              <h3 className="mt-h3">{t('rp.work_done')}</h3>
              <p className="mt-desc mt-resolution">{r.report}</p>
            </section>
          ) : null}
          {r.cancel_reason ? (
            <section>
              <h3 className="mt-h3">{t('rp.cancelled_why')}</h3>
              <p className="mt-desc">{r.cancel_reason}</p>
            </section>
          ) : null}
          <section>
            <h3 className="mt-h3">{t('rp.history')}</h3>
            <ol className="mt-events">
              {r.events.map((e, i) => (
                <li key={i}>
                  <span className="mt-event-dot" aria-hidden="true" />
                  <span>
                    <strong>{t(`rp.ev_${e.kind}` as MessageKey)}</strong>
                    {e.text ? <span> · {e.kind === 'requote' ? money(Number(e.text)) : e.text}</span> : null}
                    <br />
                    <span className="muted small">{[dateTime(e.at), e.by_name].filter(Boolean).join(' · ')}</span>
                  </span>
                </li>
              ))}
            </ol>
            <NoteAdd id={r.id} onDone={after} />
          </section>
        </div>
      </Dialog>
      {step === 'quote' ? <QuoteEditor r={r} setup={setup} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'approve' ? <ApproveDialog r={r} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'decline' ? <DeclineDialog r={r} fee={setup.settings.inspection_fee} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'invoice' ? <InvoiceDialog r={r} onClose={() => setStep(null)} onDone={after} /> : null}
      {step === 'wait' || step === 'finish' || step === 'reopen' || step === 'deliver' || step === 'cancel' ? (
        <TextStep r={r} step={step} initial={note} onClose={() => setStep(null)} onDone={after} />
      ) : null}
    </>
  );
}

function QuoteLines({ lines, totals, warranty }: { lines: Line[]; totals: Repair['totals']; warranty: boolean }) {
  return (
    <div className="rp-lines">
      <ul>
        {lines.map((l) => (
          <li key={l.id}>
            <Icon name={KIND_ICON[l.kind]} size={14} />
            <span className="rp-line-name">
              {l.name}
              <span className="muted small">
                {l.kind === 'labour' ? t('rp.hours_at', { h: num(l.qty), rate: money(l.unit_price) }) : `${num(l.qty)} × ${money(l.unit_price)}`} · {t('rp.vat', { rate: l.tax_rate })}
              </span>
            </span>
            <strong className="tabular">{money(l.net)}</strong>
          </li>
        ))}
      </ul>
      <dl className="rp-totals">
        <div>
          <dt>{t('rp.without_vat')}</dt>
          <dd className="tabular">{money(totals.net)}</dd>
        </div>
        <div>
          <dt>{t('rp.vat_total')}</dt>
          <dd className="tabular">{money(totals.tax)}</dd>
        </div>
        <div className="rp-total">
          <dt>{warranty ? t('rp.to_pay_warranty') : t('rp.to_pay')}</dt>
          <dd className="tabular">{money(totals.charge)}</dd>
        </div>
      </dl>
    </div>
  );
}

function NoteAdd({ id, onDone }: { id: string; onDone: (r: Repair) => Promise<void> }) {
  const [text, setText] = useState('');
  const { busy, run } = useSave();
  const save = () =>
    text.trim() &&
    void run(async () => {
      await onDone(await action<Repair>('repair.add_note', { id, note: text.trim() }));
      setText('');
    });
  return (
    <div className="mt-note-add">
      <input
        className="input"
        value={text}
        maxLength={300}
        placeholder={t('rp.note_placeholder')}
        aria-label={t('rp.note_placeholder')}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <Button size="sm" icon="plus" busy={busy} disabled={!text.trim()} onClick={save}>
        {t('rp.add_note')}
      </Button>
    </div>
  );
}

// ---- steps ---------------------------------------------------------------------------------------

const TEXT_STEP: Record<'wait' | 'finish' | 'reopen' | 'deliver' | 'cancel', { action: string; field: string; input: Record<string, unknown>; long: boolean; icon: IconName; danger?: boolean }> = {
  wait: { action: 'repair.progress', field: 'note', input: { to: 'waiting' }, long: false, icon: 'pause' },
  finish: { action: 'repair.finish', field: 'report', input: {}, long: true, icon: 'checkCircle' },
  reopen: { action: 'repair.reopen', field: 'reason', input: {}, long: false, icon: 'undo' },
  deliver: { action: 'repair.deliver', field: 'to', input: {}, long: false, icon: 'check' },
  cancel: { action: 'repair.cancel', field: 'reason', input: {}, long: false, icon: 'x', danger: true },
};

/** The steps that ask one thing: what it waits for, the work done, why it goes back, who collected it, why it is cancelled. */
function TextStep({ r, step, initial, onClose, onDone }: { r: Repair; step: keyof typeof TEXT_STEP; initial: string; onClose: () => void; onDone: (r: Repair) => Promise<void> }) {
  const s = TEXT_STEP[step];
  const [text, setText] = useState(initial);
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const min = step === 'deliver' ? 2 : 3;
  const error = tried && text.trim().length < min ? t(`rp.e_${step}` as MessageKey) : null;
  const save = () => {
    setTried(true);
    if (text.trim().length < min) return;
    void run(
      async () => {
        await onDone(await action<Repair>(s.action, { id: r.id, ...s.input, [s.field]: text.trim() }));
      },
      t(`rp.done_${step}` as MessageKey),
    );
  };
  return (
    <Dialog
      title={t(`rp.title_${step}` as MessageKey, { name: r.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant={s.danger ? 'danger' : 'primary'} icon={s.icon} busy={busy} onClick={save}>
            {t(`rp.go_${step}` as MessageKey)}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        {step === 'finish' && r.lines.some((l) => l.kind === 'part' && l.product) ? <p className="muted small">{t('rp.finish_stock')}</p> : null}
        {step === 'finish' && r.totals.charge > r.approved_total && !r.warranty ? <p className="callout callout-warn">{t('rp.finish_over')}</p> : null}
        {step === 'reopen' && r.parts_taken ? <p className="muted small">{t('rp.reopen_stock')}</p> : null}
        {step === 'deliver' && r.accessories ? (
          <p className="callout callout-info">
            <Icon name="box" size={16} />
            <span>{t('rp.give_back', { what: r.accessories })}</span>
          </p>
        ) : null}
        <Field label={t(`rp.label_${step}` as MessageKey)} required error={error} counter={s.long ? `${text.length}/4000` : undefined}>
          {s.long ? (
            <textarea className="input" rows={5} value={text} maxLength={4000} onChange={(e) => setText(e.target.value)} data-autofocus />
          ) : (
            <input className="input" value={text} maxLength={step === 'deliver' ? 80 : 300} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} data-autofocus />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function ApproveDialog({ r, onClose, onDone }: { r: Repair; onClose: () => void; onDone: (r: Repair) => Promise<void> }) {
  const [by, setBy] = useState(r.contact ?? r.customer_name);
  const [via, setVia] = useState<Via>(r.state === 'received' ? 'in_shop' : 'phone');
  const { busy, run } = useSave();
  const bad = by.trim().length < 2;
  return (
    <Dialog
      title={t('rp.title_approve', { name: r.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={bad}
            onClick={() => void run(async () => onDone(await action<Repair>('repair.approve', { id: r.id, by: by.trim(), via })), t('rp.done_approve'))}
          >
            {t('rp.go_approve')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="rp-agree">
          {t('rp.agree_to')} <strong className="tabular">{money(r.totals.charge)}</strong>
        </p>
        <Field label={t('rp.who_said_yes')} required error={bad ? t('rp.e_who') : null}>
          <input className="input" value={by} maxLength={80} onChange={(e) => setBy(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('rp.how')}>
          <div className="segmented" role="radiogroup" aria-label={t('rp.how')}>
            {VIAS.map((v) => (
              <button key={v} type="button" role="radio" aria-checked={via === v} className={via === v ? 'active' : ''} onClick={() => setVia(v)}>
                {t(`rp.via_${v}` as MessageKey)}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </Dialog>
  );
}

function DeclineDialog({ r, fee, onClose, onDone }: { r: Repair; fee: number; onClose: () => void; onDone: (r: Repair) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [charge, setCharge] = useState(fee > 0 && !r.warranty);
  const { busy, run } = useSave();
  return (
    <Dialog
      title={t('rp.title_decline', { name: r.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button
            variant="primary"
            icon="x"
            busy={busy}
            onClick={() => void run(async () => onDone(await action<Repair>('repair.decline', { id: r.id, note: note.trim() || null, charge_inspection: charge })), t('rp.done_decline'))}
          >
            {t('rp.go_decline')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('rp.decline_body')}</p>
        {fee > 0 && !r.warranty ? (
          <div className="setting-row">
            <div>
              <strong>{t('rp.charge_inspection')}</strong>
              <p className="muted">{t('rp.charge_inspection_hint', { amount: money(fee) })}</p>
            </div>
            <Switch checked={charge} onChange={setCharge} label={t('rp.charge_inspection')} />
          </div>
        ) : null}
        <Field label={t('rp.decline_note')}>
          <input className="input" value={note} maxLength={300} placeholder={t('rp.decline_note_example')} onChange={(e) => setNote(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function InvoiceDialog({ r, onClose, onDone }: { r: Repair; onClose: () => void; onDone: (r: Repair) => Promise<void> }) {
  const { busy, run } = useSave();
  const make = (issue: boolean) => void run(async () => onDone(await action<Repair>('repair.invoice', { id: r.id, issue })), issue ? t('rp.done_invoice') : t('rp.done_draft'));
  return (
    <Dialog
      title={t('rp.title_invoice', { name: r.name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button icon="file" busy={busy} onClick={() => make(false)}>
            {t('rp.as_draft')}
          </Button>
          <Button variant="primary" icon="invoice" busy={busy} onClick={() => make(true)}>
            {t('rp.issue_now')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p>{t('rp.invoice_body', { customer: r.customer_name, total: money(r.totals.charge) })}</p>
        <QuoteLines lines={r.lines} totals={r.totals} warranty={false} />
      </div>
    </Dialog>
  );
}

// ---- the quote -------------------------------------------------------------------------------------

interface Draft {
  key: number;
  kind: LineKind;
  product: string | null;
  name: string;
  qty: string;
  price: string;
  tax_rate: number;
}

function QuoteEditor({ r, setup, onClose, onDone }: { r: Repair; setup: Setup; onClose: () => void; onDone: (r: Repair) => Promise<void> }) {
  const s = setup.settings;
  const [diagnosis, setDiagnosis] = useState(r.diagnosis ?? '');
  const [seq, setSeq] = useState(r.lines.length);
  const [lines, setLines] = useState<Draft[]>(() =>
    r.lines.map((l, i) => ({ key: i, kind: l.kind, product: l.product, name: l.name, qty: String(l.qty), price: amountText(l.unit_price), tax_rate: l.tax_rate })),
  );
  const [pick, setPick] = useState('');
  const [tried, setTried] = useState(false);
  const { busy, run } = useSave();
  const add = (l: Omit<Draft, 'key'>) => {
    setLines((all) => [...all, { ...l, key: seq }]);
    setSeq((n) => n + 1);
  };
  const change = (key: number, patch: Partial<Draft>) => setLines((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const parsed = lines.map((l) => {
    const qty = Number(l.qty.replace(',', '.'));
    const price = l.price.trim() ? parseMoney(l.price) : 0;
    return { ...l, q: Number.isFinite(qty) && qty > 0 ? qty : null, p: price };
  });
  const bad = parsed.some((l) => l.q === null || l.p === null || !l.name.trim());
  const totals = useMemo(() => {
    let net = 0;
    let tax = 0;
    for (const l of parsed) {
      if (l.q === null || l.p === null) continue;
      const n = Math.round(l.p * l.q);
      net += n;
      tax += Math.round((n * l.tax_rate) / 100);
    }
    return { net, tax, total: net + tax };
  }, [parsed]);
  const over = !r.warranty && ['approved', 'repairing', 'waiting'].includes(r.state) && totals.total > r.approved_total;
  const save = () => {
    setTried(true);
    if (bad) return;
    void run(
      async () => {
        await onDone(
          await action<Repair>('repair.save_quote', {
            id: r.id,
            diagnosis: diagnosis.trim() || null,
            lines: parsed.map((l) => ({ kind: l.kind, product: l.product, name: l.name.trim(), qty: l.q, unit_price: l.p, tax_rate: l.tax_rate })),
          }),
        );
      },
      over ? t('rp.done_requote') : t('rp.done_quote'),
    );
  };
  return (
    <Dialog
      title={t('rp.title_quote', { name: r.name })}
      onClose={onClose}
      size="xl"
      footer={
        <span className="foot-actions rp-foot">
          <span className="rp-quote-total">
            {t('rp.to_pay')} <strong className="tabular">{money(r.warranty ? 0 : totals.total)}</strong>
          </span>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('rp.save_quote')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('rp.diagnosis')} hint={t('rp.diagnosis_hint')} counter={`${diagnosis.length}/4000`}>
          <textarea className="input" rows={3} value={diagnosis} maxLength={4000} onChange={(e) => setDiagnosis(e.target.value)} data-autofocus />
        </Field>
        {over ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('rp.over_hint', { agreed: money(r.approved_total) })}</span>
          </p>
        ) : null}
        <div className="rp-add">
          <select className="input" value={pick} aria-label={t('rp.add_part')} onChange={(e) => setPick(e.target.value)}>
            <option value="">{t('rp.choose_part')}</option>
            {setup.products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {money(p.price)}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            icon="box"
            disabled={!pick}
            onClick={() => {
              const p = setup.products.find((x) => x.id === pick);
              if (!p) return;
              add({ kind: 'part', product: p.id, name: p.name, qty: '1', price: amountText(p.net), tax_rate: p.tax_rate });
              setPick('');
            }}
          >
            {t('rp.add_part')}
          </Button>
          <Button size="sm" icon="clock" onClick={() => add({ kind: 'labour', product: null, name: s.labour_name, qty: '1', price: amountText(s.hourly_rate), tax_rate: s.labour_tax_rate })}>
            {t('rp.add_labour')}
          </Button>
          <Button size="sm" icon="plus" onClick={() => add({ kind: 'part', product: null, name: '', qty: '1', price: '', tax_rate: standardRateNow() })}>
            {t('rp.add_other')}
          </Button>
        </div>
        {!lines.length ? (
          <p className="muted small">{t('rp.quote_empty')}</p>
        ) : (
          <div className="rp-edit-lines" role="table" aria-label={t('rp.quote')}>
            <div className="rp-edit-head muted small" role="row">
              <span role="columnheader">{t('rp.l_kind')}</span>
              <span role="columnheader">{t('rp.l_name')}</span>
              <span role="columnheader">{t('rp.l_qty')}</span>
              <span role="columnheader">{t('rp.l_price')}</span>
              <span role="columnheader">{t('rp.l_vat')}</span>
              <span role="columnheader" className="tabular">
                {t('rp.l_amount')}
              </span>
              <span />
            </div>
            {parsed.map((l) => (
              <div key={l.key} className="rp-edit-line" role="row">
                <select className="input" value={l.kind} aria-label={t('rp.l_kind')} onChange={(e) => change(l.key, { kind: e.target.value as LineKind })}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`rp.kind_${k}` as MessageKey)}
                    </option>
                  ))}
                </select>
                <input
                  className="input"
                  aria-invalid={tried && !l.name.trim() ? true : undefined}
                  value={l.name}
                  maxLength={200}
                  aria-label={t('rp.l_name')}
                  placeholder={t('rp.l_name_example')}
                  onChange={(e) => change(l.key, { name: e.target.value, product: e.target.value === l.name ? l.product : null })}
                />
                <input
                  className="input"
                  aria-invalid={l.q === null ? true : undefined}
                  inputMode="decimal"
                  value={l.qty}
                  aria-label={l.kind === 'labour' ? t('rp.l_hours') : t('rp.l_qty')}
                  onChange={(e) => change(l.key, { qty: e.target.value })}
                />
                <input
                  className="input"
                  aria-invalid={l.p === null ? true : undefined}
                  inputMode="decimal"
                  value={l.price}
                  aria-label={t('rp.l_price')}
                  onChange={(e) => change(l.key, { price: e.target.value })}
                />
                <TaxRateSelect value={l.tax_rate} label={t('rp.l_vat')} onChange={(r) => change(l.key, { tax_rate: r })} />
                <strong className="tabular rp-edit-amount">{l.q !== null && l.p !== null ? money(Math.round(l.p * l.q)) : '—'}</strong>
                <IconButton icon="trash" label={t('rp.remove_line')} onClick={() => setLines((all) => all.filter((x) => x.key !== l.key))} />
              </div>
            ))}
            <dl className="rp-totals">
              <div>
                <dt>{t('rp.without_vat')}</dt>
                <dd className="tabular">{money(totals.net)}</dd>
              </div>
              <div>
                <dt>{t('rp.vat_total')}</dt>
                <dd className="tabular">{money(totals.tax)}</dd>
              </div>
              <div className="rp-total">
                <dt>{r.warranty ? t('rp.to_pay_warranty') : t('rp.to_pay')}</dt>
                <dd className="tabular">{money(r.warranty ? 0 : totals.total)}</dd>
              </div>
            </dl>
          </div>
        )}
        <p className="muted small">{setup.stock ? t('rp.prices_note_stock') : t('rp.prices_note')}</p>
      </div>
    </Dialog>
  );
}

// ---- taking in -------------------------------------------------------------------------------------

function ReceiveForm({ id, setup, onClose, onSaved }: { id: string | null; setup: Setup; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const [ready, setReady] = useState(!id);
  const [tried, setTried] = useState(false);
  const [f, setF] = useState({
    customer: '',
    newName: '',
    newPhone: '',
    contact: '',
    phone: '',
    device: '',
    model: '',
    serial: '',
    accessories: '',
    problem: '',
    priority: 'normal' as 'normal' | 'urgent',
    warranty: false,
    warranty_note: '',
    technician: '',
    promised: '',
    location: setup.locations[0]?.id ?? '',
  });
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!id) return;
    void action<Repair>('repair.get', { id }).then((r) => {
      setF((x) => ({
        ...x,
        customer: r.customer,
        contact: r.contact ?? '',
        phone: r.phone && r.phone !== setup.customers.find((c) => c.id === r.customer)?.phone ? r.phone : '',
        device: r.device,
        model: r.model ?? '',
        serial: r.serial ?? '',
        accessories: r.accessories ?? '',
        problem: r.problem,
        priority: r.priority,
        warranty: r.warranty,
        warranty_note: r.warranty_note ?? '',
        technician: r.technician ?? '',
        promised: r.promised ?? '',
        location: r.location ?? x.location,
      }));
      setReady(true);
    });
  }, [id]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const cust = setup.customers.find((c) => c.id === f.customer);
  const found = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || cust) return [];
    const digits = q.replace(/\D/g, '');
    return setup.customers.filter((c) => c.name.toLowerCase().includes(q) || (digits.length >= 3 && (c.phone ?? '').replace(/\D/g, '').includes(digits))).slice(0, 6);
  }, [search, cust, setup.customers]);
  const errors = {
    customer: !f.customer && !(adding && f.newName.trim().length >= 2) ? (adding ? t('rp.e_new_name') : t('rp.e_customer')) : null,
    device: f.device.trim().length < 2 ? t('rp.e_device') : null,
    problem: f.problem.trim().length < 3 ? t('rp.e_problem') : null,
    warranty: f.warranty && !f.warranty_note.trim() && !f.serial.trim() ? t('rp.e_warranty') : null,
  };
  const err = (e: string | null) => (tried ? e : null);
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const body = {
      contact: f.contact.trim() || null,
      phone: f.phone.trim() || null,
      device: f.device.trim(),
      model: f.model.trim() || null,
      serial: f.serial.trim() || null,
      accessories: f.accessories.trim() || null,
      problem: f.problem.trim(),
      priority: f.priority,
      warranty: f.warranty,
      warranty_note: f.warranty_note.trim() || null,
      technician: f.technician || null,
      promised: f.promised || null,
      location: f.location || null,
    };
    void run(
      async () => {
        const r = id
          ? await action<Repair>('repair.save', { id, ...body })
          : await action<Repair>('repair.receive', { ...body, customer: f.customer || null, new_customer: f.customer ? null : { name: f.newName.trim(), phone: f.newPhone.trim() || null } });
        await onSaved(r.id);
      },
      id ? t('rp.saved') : t('rp.received_done'),
    );
  };
  return (
    <Dialog
      title={id ? t('rp.edit_title') : t('rp.new')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {id ? t('rp.save') : t('rp.take_in')}
          </Button>
        </span>
      }
    >
      {!ready ? (
        <Skeleton h={420} />
      ) : (
        <div className="form-stack sub-form">
          <Field label={t('rp.customer')} required error={err(errors.customer)} hint={cust?.phone ?? undefined}>
            {cust ? (
              <div className="fs-picked">
                <strong>{cust.name}</strong>
                {!id ? <IconButton icon="x" label={t('rp.change')} onClick={() => (set('customer', ''), setSearch(''))} /> : null}
              </div>
            ) : adding ? (
              <div className="form-grid rp-new-customer">
                <input
                  className="input"
                  value={f.newName}
                  maxLength={120}
                  placeholder={t('rp.new_name')}
                  aria-label={t('rp.new_name')}
                  onChange={(e) => set('newName', e.target.value)}
                  data-autofocus
                />
                <input
                  className="input"
                  type="tel"
                  value={f.newPhone}
                  maxLength={40}
                  placeholder={t('rp.new_phone')}
                  aria-label={t('rp.new_phone')}
                  onChange={(e) => set('newPhone', e.target.value)}
                />
              </div>
            ) : (
              <label className="search">
                <Icon name="search" size={16} />
                <input className="input" type="search" value={search} placeholder={t('rp.search_customers')} onChange={(e) => setSearch(e.target.value)} data-autofocus />
              </label>
            )}
          </Field>
          {!cust && !id ? (
            adding ? (
              <p className="small">
                <button className="link" type="button" onClick={() => setAdding(false)}>
                  {t('rp.pick_existing')}
                </button>
              </p>
            ) : (
              <>
                {found.length ? (
                  <ul className="shop-ways">
                    {found.map((c) => (
                      <li key={c.id}>
                        <span className="vat-party">
                          <span>{c.name}</span>
                          <span className="muted small">{c.phone ?? t('rp.no_phone')}</span>
                        </span>
                        <Button size="sm" icon="check" onClick={() => set('customer', c.id)}>
                          {t('rp.choose')}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="small">
                  <button
                    className="link"
                    type="button"
                    onClick={() => {
                      setAdding(true);
                      const q = search.trim();
                      if (/^\+?[\d\s]{6,}$/.test(q)) set('newPhone', q);
                      else set('newName', q);
                    }}
                  >
                    {search.trim() && !found.length ? t('rp.add_customer_named', { name: search.trim() }) : t('rp.add_customer')}
                  </button>
                </p>
              </>
            )
          ) : null}
          <div className="form-grid">
            <Field label={t('rp.what')} required error={err(errors.device)}>
              <input className="input" list="rp-devices" value={f.device} maxLength={120} placeholder={t('rp.what_example')} onChange={(e) => set('device', e.target.value)} />
            </Field>
            <Field label={t('rp.model')}>
              <input className="input" value={f.model} maxLength={120} placeholder={t('rp.model_example')} onChange={(e) => set('model', e.target.value)} />
            </Field>
          </div>
          <datalist id="rp-devices">
            {setup.devices.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
          <div className="form-grid">
            <Field label={t('rp.serial')} hint={t('rp.serial_hint')}>
              <input className="input" value={f.serial} maxLength={80} onChange={(e) => set('serial', e.target.value)} />
            </Field>
            <Field label={t('rp.accessories')} hint={t('rp.accessories_hint')}>
              <input className="input" value={f.accessories} maxLength={200} placeholder={t('rp.accessories_example')} onChange={(e) => set('accessories', e.target.value)} />
            </Field>
          </div>
          <Field label={t('rp.problem')} required error={err(errors.problem)} counter={`${f.problem.length}/4000`}>
            <textarea className="input" rows={3} value={f.problem} maxLength={4000} placeholder={t('rp.problem_example')} onChange={(e) => set('problem', e.target.value)} />
          </Field>
          <div className="form-grid">
            <Field label={t('rp.priority')}>
              <div className="segmented mt-prio" role="radiogroup" aria-label={t('rp.priority')}>
                {(['normal', 'urgent'] as const).map((p) => (
                  <button key={p} type="button" role="radio" aria-checked={f.priority === p} className={`${f.priority === p ? 'active' : ''} mt-prio-${p}`} onClick={() => set('priority', p)}>
                    {t(`rp.prio_${p}` as MessageKey)}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={t('rp.promised')} hint={!f.promised ? tn('rp.promised_default', setup.settings.promise_days) : undefined}>
              <input className="input" type="date" value={f.promised} onChange={(e) => set('promised', e.target.value)} />
            </Field>
          </div>
          <div className="form-grid">
            <Field label={t('rp.technician')}>
              <select className="input" value={f.technician} onChange={(e) => set('technician', e.target.value)}>
                <option value="">{t('rp.nobody_yet')}</option>
                {setup.technicians.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
            {setup.locations.length > 1 ? (
              <Field label={t('rp.location')}>
                <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
                  {setup.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
          </div>
          <div className="form-grid">
            <Field label={t('rp.contact')} hint={t('rp.contact_hint')}>
              <input className="input" value={f.contact} maxLength={80} onChange={(e) => set('contact', e.target.value)} />
            </Field>
            <Field label={t('rp.phone')} hint={t('rp.phone_hint')}>
              <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
            </Field>
          </div>
          <div className="setting-row">
            <div>
              <strong>{t('rp.under_warranty')}</strong>
              <p className="muted">{t('rp.under_warranty_hint')}</p>
            </div>
            <Switch checked={f.warranty} onChange={(v) => set('warranty', v)} label={t('rp.under_warranty')} />
          </div>
          {f.warranty ? (
            <Field label={t('rp.warranty_note')} hint={t('rp.warranty_hint')} error={err(errors.warranty)}>
              <input className="input" value={f.warranty_note} maxLength={200} placeholder={t('rp.warranty_example')} onChange={(e) => set('warranty_note', e.target.value)} />
            </Field>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

// ---- report and settings ---------------------------------------------------------------------------

interface Report {
  total: { received: number; delivered: number; warranty: number; declined: number; turnaround: number | null; on_time: number; promised: number; invoiced: number; invoices: number };
  people: { id: string | null; name: string; done: number; days: number | null }[];
  devices: { name: string; n: number; warranty: number }[];
  parts: { name: string; qty: number; repairs: number }[];
}

function ReportTab({ today }: { today: string }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const from = `${month}-01`;
  const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const to = last > today ? today : last;
  const { data } = useAction<Report>('repair.report', { from, to: to < from ? from : to });
  const onTime = data && data.total.promised ? Math.round((data.total.on_time / data.total.promised) * 100) : null;
  return (
    <div className="lu-report">
      <div className="lu-day-tools">
        <input className="input lu-date" type="month" value={month} max={today.slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t('rp.month')} />
        <Button
          size="sm"
          icon="download"
          disabled={!data?.devices.length}
          onClick={() =>
            data &&
            downloadCsv(
              `repairs-${month}.csv`,
              [t('rp.c_item'), t('rp.r_received'), t('rp.r_warranty')],
              data.devices.map((d) => [d.name, d.n, d.warranty]),
            )
          }
        >
          {t('rp.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={300} />
      ) : (
        <>
          <section className="rep-kpis">
            <Kpi label={t('rp.r_received')} value={String(data.total.received)} hint={tn('rp.r_declined', data.total.declined)} />
            <Kpi label={t('rp.r_delivered')} value={String(data.total.delivered)} hint={tn('rp.r_warranty_n', data.total.warranty)} />
            <Kpi label={t('rp.r_on_time')} value={onTime === null ? '—' : `${onTime}%`} hint={t('rp.r_on_time_hint')} warn={onTime !== null && onTime < 80} />
            <Kpi label={t('rp.r_turnaround')} value={days(data.total.turnaround)} hint={t('rp.r_turnaround_hint')} />
            <Kpi label={t('rp.r_invoiced')} value={money(data.total.invoiced)} hint={tn('rp.r_invoices', data.total.invoices)} />
          </section>
          <div className="mt-report-grid">
            <section className="panel">
              <h2 className="rep-sub">{t('rp.r_people')}</h2>
              <TableWrap>
                <Table
                  head={[t('rp.technician'), t('rp.r_done'), t('rp.r_bench')]}
                  empty={t('rp.r_none')}
                  rows={data.people.map((p, i) => ({ key: p.id ?? `x${i}`, cells: [p.name, String(p.done), days(p.days)] }))}
                />
              </TableWrap>
            </section>
            <section className="panel">
              <h2 className="rep-sub">{t('rp.r_items')}</h2>
              <TableWrap>
                <Table
                  head={[t('rp.c_item'), t('rp.r_received'), t('rp.r_warranty')]}
                  empty={t('rp.r_none')}
                  rows={data.devices.map((d) => ({ key: d.name, cells: [d.name, String(d.n), String(d.warranty)] }))}
                />
              </TableWrap>
            </section>
            <section className="panel">
              <h2 className="rep-sub">{t('rp.r_parts')}</h2>
              <TableWrap>
                <Table
                  head={[t('rp.r_part'), t('rp.l_qty'), t('rp.r_repairs')]}
                  empty={t('rp.r_none')}
                  rows={data.parts.map((p) => ({ key: p.name, cells: [p.name, num(p.qty), String(p.repairs)] }))}
                />
              </TableWrap>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data, reload } = useAction<Setup>('repair.setup');
  const [f, setF] = useState<(Settings & { rate: string; fee: string }) | null>(null);
  useEffect(() => {
    if (data && !f) setF({ ...data.settings, rate: amountText(data.settings.hourly_rate), fee: amountText(data.settings.inspection_fee) });
  }, [data]);
  if (!f) return <Skeleton h={300} />;
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const rate = f.rate.trim() ? parseMoney(f.rate) : 0;
  const fee = f.fee.trim() ? parseMoney(f.fee) : 0;
  const bad = rate === null || fee === null || !f.labour_name.trim() || !f.inspection_name.trim();
  return (
    <div className="form-stack">
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('rp.s_prices')}</h2>
          <p className="muted small">{t('rp.s_prices_hint')}</p>
        </div>
        <div className="form-grid">
          <Field label={t('rp.s_rate')} hint={t('rp.before_vat')} error={rate === null ? t('rp.e_amount') : null}>
            <input className="input" inputMode="decimal" value={f.rate} onChange={(e) => set('rate', e.target.value)} />
          </Field>
          <Field label={t('rp.s_labour_name')} hint={t('rp.on_invoice')}>
            <input className="input" value={f.labour_name} maxLength={80} onChange={(e) => set('labour_name', e.target.value)} />
          </Field>
          <Field label={t('rp.l_vat')}>
            <TaxRateSelect value={f.labour_tax_rate} onChange={(r) => set('labour_tax_rate', r)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label={t('rp.s_fee')} hint={t('rp.s_fee_hint')} error={fee === null ? t('rp.e_amount') : null}>
            <input className="input" inputMode="decimal" value={f.fee} onChange={(e) => set('fee', e.target.value)} />
          </Field>
          <Field label={t('rp.s_fee_name')} hint={t('rp.on_invoice')}>
            <input className="input" value={f.inspection_name} maxLength={80} onChange={(e) => set('inspection_name', e.target.value)} />
          </Field>
          <Field label={t('rp.s_days')} hint={t('rp.s_days_hint')}>
            <select className="input" value={f.promise_days} onChange={(e) => set('promise_days', Number(e.target.value))}>
              {[0, 1, 2, 3, 4, 5, 7, 10, 14, 21, 30].map((d) => (
                <option key={d} value={d}>
                  {d ? tn('rp.n_days', d) : t('rp.same_day')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="row-actions mkt-settings-foot">
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={bad}
            onClick={() =>
              void run(async () => {
                await action('repair.save_settings', {
                  hourly_rate: rate,
                  labour_name: f.labour_name.trim(),
                  labour_tax_rate: f.labour_tax_rate,
                  inspection_fee: fee,
                  inspection_name: f.inspection_name.trim(),
                  promise_days: f.promise_days,
                });
                await reload();
                await onChange();
              }, t('rp.settings_saved'))
            }
          >
            {t('rp.save')}
          </Button>
        </div>
      </section>
    </div>
  );
}
